import { Stack, darken } from '@mui/material';
import config from 'config/default';
import { Genres } from 'enums';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../ui/base-components/pai-button';
import PaiDotLoader from '../ui/base-components/pai-dot-loader';
import PaiLink from '../ui/base-components/pai-link';
import PaiMenuItem from '../ui/base-components/pai-menu-item';
import PaiSelect from '../ui/base-components/pai-select';
import PaiTextArea from '../ui/base-components/pai-text-area';
import genresConfig, { TextGenerationCredentials } from './genres-config';

interface Props {
  hideTextGeneration: boolean;

  selectedLanguage: string;

  genre: Genres | null;
  textGenerationCredentials: TextGenerationCredentials;
  generatedToc: string;
  tocGenerated: boolean;

  tocGenerationLoading: boolean;

  handleToggleTocAccepted(): void;

  toggleHideTextGeneration(): void;

  handleSelectedLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleTextGenerationCredentialsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleGeneratedTocChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleGenerateToc(): void;

  handleAcceptToc(): void;
}

const TocGenerationInputs: React.FC<Props> = ({
  hideTextGeneration,
  selectedLanguage,
  genre,
  textGenerationCredentials,
  generatedToc,
  tocGenerated,
  tocGenerationLoading,
  handleToggleTocAccepted,
  toggleHideTextGeneration,
  handleSelectedLanguageChange,
  handleTextGenerationCredentialsChange,
  handleGeneratedTocChange,
  handleGenerateToc,
  handleAcceptToc,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const tocGenre = genre === Genres.BLOG_POST ? Genres.BLOG_POST_TOC : genre === Genres.LANDING_PAGE ? Genres.LANDING_PAGE_TOC : undefined;

  const hasEmptyField = useMemo(() => {
    for (const { genres, requiredFields } of genresConfig) {
      if (!_.isNil(tocGenre) && genres.includes(tocGenre)) {
        for (const field of requiredFields) {
          const value = _.get(textGenerationCredentials, field.valueKey);
          if (value === '' || value === undefined) {
            return true;
          }
        }
      }
    }
    return false;
  }, [tocGenre, textGenerationCredentials]);

  return (
    <div className={classes.column}>
      {!hideTextGeneration ? (
        <div className={classes.textGenToggleContainer}>
          <PaiLink text={t('useOwnText')} size={'md'} underline={'always'} action={toggleHideTextGeneration} />
        </div>
      ) : null}
      <Stack gap={1} sx={{ height: '100%' }}>
        <Stack className={classes.textGenerationInputs} gap={1}>
          <PaiSelect value={selectedLanguage ?? ''} disableBorder={true} onChange={handleSelectedLanguageChange} disabled={tocGenerationLoading}>
            {config.textGenerationLanguages.map((lang, index) => (
              <PaiMenuItem key={index} value={lang.key ?? ''}>
                {t('language')}: {lang.text}
              </PaiMenuItem>
            ))}
          </PaiSelect>
          <Stack gap={1}>
            {!tocGenerated ? (
              <>
                {genresConfig.map(({ genres, requiredFields }) =>
                  !_.isNil(tocGenre) && genres.includes(tocGenre)
                    ? requiredFields.map((field) => (
                        <PaiTextArea
                          key={field.valueKey}
                          value={_.get(textGenerationCredentials, field.valueKey) || ''}
                          name={field.nameKey}
                          disabled={tocGenerationLoading}
                          onChange={handleTextGenerationCredentialsChange}
                          instruction={field.instruction}
                          placeholder={field.placeholder}
                          rows={field.rows}
                          disableBorder={true}
                        />
                      ))
                    : null
                )}
                {genresConfig.map(({ genres, optionalFields }) =>
                  !_.isNil(tocGenre) && genres.includes(tocGenre)
                    ? optionalFields.map((field) => (
                        <PaiTextArea
                          key={field.valueKey}
                          value={_.get(textGenerationCredentials, field.valueKey) || ''}
                          name={field.nameKey}
                          optional={true}
                          disabled={tocGenerationLoading}
                          onChange={handleTextGenerationCredentialsChange}
                          instruction={field.instruction}
                          placeholder={field.placeholder}
                          rows={field.rows}
                          disableBorder={true}
                        />
                      ))
                    : null
                )}
              </>
            ) : (
              <PaiTextArea
                value={generatedToc}
                disabled={tocGenerationLoading}
                onChange={handleGeneratedTocChange}
                rows={null}
                disableBorder={true}
              />
            )}
          </Stack>
        </Stack>
        {tocGenerationLoading ? (
          <PaiDotLoader size="normal" />
        ) : !tocGenerated ? (
          <Stack gap={1}>
            <PaiLink
              text={t('useOwnToC')}
              variant={'primary'}
              underline={'always'}
              size={'md'}
              action={handleToggleTocAccepted}
            />
            <PaiButton text={t('generateTableOfContents')} action={handleGenerateToc} size={'large'} disabled={hasEmptyField || false} />
          </Stack>
        ) : (
          <PaiButton text={t('createContent')} action={handleAcceptToc} size={'large'} disabled={generatedToc === ''} />
        )}
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  textGenToggleContainer: {
    position: 'absolute',
    top: '-28px',
    left: 0,
  },
  column: {
    position: 'relative',
    width: '350px',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    padding: '16px 16px 8px 16px',
    '&.collapsed': {
      cursor: 'pointer',
      maxWidth: '56px',
      padding: 0,
      border: `2px solid ${darken('#FFF', 0.05)}`,
      [theme.breakpoints.down('xl')]: {
        maxWidth: 'unset',
        height: '56px',
      },
    },
    [theme.breakpoints.down('xl')]: {
      height: '640px',
      width: '100%',
    },
    [theme.breakpoints.down('lg')]: {
      height: 'unset',
      width: '100%',
    },
  },
  textGenerationInputs: {
    height: 'calc(100% - 45px)',
    padding: '16px',
    overflow: 'auto',
  },
}));

export default TocGenerationInputs;
